"use client";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import "./VideoCard.scss";

function VideoCard({
  video,
  variant = "default",
  isActive = false,
  onPlay = () => {},
  className = "",
  style = {},
}) {
  const [localShowVideo, setLocalShowVideo] = useState(false);
  const isPartnerVariant = variant === "partner";

  // Show video if this card is active or was previously showing video locally
  const showVideo = isActive || localShowVideo;

  // Reset local show state when isActive changes
  useEffect(() => {
    if (!isActive) {
      const timeoutId = setTimeout(() => {
        setLocalShowVideo(false);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [isActive]);

  const getVideoId = useCallback((url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/
    );
    return match ? match[1] : null;
  }, []);

  // Handle different video data structures
  const { videoUrl, videoTitle, videoThumbnail } = useMemo(
    () => ({
      videoUrl: video?.url || video?.videoLink || video?.attributes?.videoLink,
      videoTitle: video?.title || video?.heading || video?.attributes?.title,
      videoThumbnail:
        video?.thumbnail ||
        video?.img?.url ||
        video?.attributes?.img?.data?.attributes?.url,
    }),
    [video]
  );

  const videoId = useMemo(() => getVideoId(videoUrl), [getVideoId, videoUrl]);
  const thumbnailUrl = useMemo(
    () =>
      videoThumbnail ||
      (videoId
        ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        : "/default-thumbnail.jpg"),
    [videoThumbnail, videoId]
  );
  const embedUrl = useMemo(
    () =>
      videoId
        ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&fs=0&disablekb=1&playsinline=1&cc_load_policy=0&cc_lang_pref=en&widget_referrer=${encodeURIComponent(window.location.origin)}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
        : "",
    [videoId]
  );

  const handlePlay = useCallback(
    (e) => {
      e.stopPropagation();
      setLocalShowVideo(true);
      onPlay?.();
    },
    [onPlay]
  );

  if (!videoId && !videoThumbnail) return null;

  return (
    <div
      className={`w-full video-card ${isPartnerVariant ? "max-w-[60px] sm:max-w-[80px] md:max-w-[120px] lg:max-w-[180px] xl:max-w-[240px]" : "max-w-[50px] sm:max-w-[70px] md:max-w-[100px] lg:max-w-[150px] xl:max-w-[200px]"} ${className} bg-white rounded-[3px] sm:rounded-[4px] md:rounded-[6px] lg:rounded-[4px] xl:rounded-[12px] shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100 p-1 sm:p-1 md:p-2 lg:p-6`}
      style={{
        width: isPartnerVariant ? "calc(100% - 4px)" : "calc(100% - 2px)",
        minWidth: "50px",
        maxWidth: isPartnerVariant ? "450px" : "400px",
        ...style,
      }}
    >
      <div
        className={`relative video-container ${isPartnerVariant ? "partner-variant" : ""}`}
      >
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          {!showVideo ? (
            <>
              <Image
                src={thumbnailUrl}
                alt={videoTitle}
                fill
                className="object-cover rounded-[4px] sm:rounded-[6px] md:rounded-[8px] lg:rounded-[20px]"
                unoptimized={thumbnailUrl.startsWith("http")}
              />
              <div className="absolute inset-0 bg-black/10 rounded-[4px] sm:rounded-[6px] md:rounded-[8px] lg:rounded-[20px]" />
              <button
                onClick={handlePlay}
                className="absolute inset-0 w-full h-full flex items-center justify-center"
                aria-label="Play video"
              >
                <svg
                  width={isPartnerVariant ? "54" : "36"}
                  height={isPartnerVariant ? "57" : "39"}
                  viewBox="0 0 54 57"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 xl:w-14 xl:h-14"
                >
                  <path
                    d="M48.767 21.108C54.6906 24.3293 54.6906 32.668 48.767 35.8894L13.0002 55.3392C7.24303 58.47 0.167969 54.395 0.167969 47.9485V9.04888C0.167969 2.6023 7.24303 -1.47258 13.0002 1.65816L48.767 21.108Z"
                    fill="white"
                  />
                </svg>
              </button>
            </>
          ) : (
            <div className="absolute inset-0 w-full h-full overflow-hidden rounded-[4px] sm:rounded-[6px] md:rounded-[8px] lg:rounded-[20px] bg-black">
              <iframe
                src={embedUrl}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
                title={videoTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
      </div>
      <div className="mt-0.5 sm:mt-1 md:mt-2 px-1 sm:px-2 md:px-3 lg:px-5 h-2 sm:h-3 md:h-4 flex items-center overflow-hidden">
        <p className="text-[8px] sm:text-[10px] md:text-xs lg:text-[15px] text-gray-800 font-medium truncate w-full px-0.5">
          {videoTitle}
        </p>
      </div>
    </div>
  );
}

export default memo(VideoCard);
